import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import i18n from 'i18n-react';
import { isNil } from 'lodash-es';

import type { FreeHotelItem } from 'hooks/useDirectoryData/types';

export type AmenityColumn =
  | 'restaurants'
  | 'bars'
  | 'pools'
  | 'golf'
  | 'beachfront'
  | 'spa'
  | 'meeting_room'
  | 'gym'
  | 'private_airstrip'
  | 'fishing_lodge'
  | 'dive_center'
  | 'casino'
  | 'selfCatering'
  | 'kitchenette_extended_stay'
  | 'marina'
  | 'waterfront'
  | 'accessible_property'
  | 'villa_product'
  | 'all_suite'
  | 'family_friendly'
  | 'non_smoking_resort'
  | 'downtown'
  | 'on_strip'
  | 'four_star'
  | 'five_star'
  | 'four_star_diamond'
  | 'five_star_diamond';

export interface Amenity {
  column: AmenityColumn;
  label: string;
  icon: IconProp;
}

// The order here is the order on the directory card and the profile.
const AMENITIES: { column: AmenityColumn; icon: IconProp }[] = [
  { column: 'restaurants', icon: ['fas', 'utensils'] },
  { column: 'bars', icon: ['fas', 'glass-martini'] },
  { column: 'pools', icon: ['fas', 'swimming-pool'] },
  { column: 'golf', icon: ['fas', 'golf-ball-tee'] },
  { column: 'beachfront', icon: ['fas', 'umbrella-beach'] },
  { column: 'spa', icon: ['fas', 'spa'] },
  { column: 'meeting_room', icon: ['fas', 'handshake'] },
  { column: 'gym', icon: ['fas', 'dumbbell'] },
  { column: 'private_airstrip', icon: ['fas', 'plane'] },
  { column: 'fishing_lodge', icon: ['fas', 'fish'] },
  { column: 'dive_center', icon: ['fas', 'mask-snorkel'] },
  { column: 'casino', icon: ['fas', 'dice'] },
  { column: 'selfCatering', icon: ['fas', 'oven'] },
  { column: 'kitchenette_extended_stay', icon: ['fas', 'microwave'] },
  { column: 'marina', icon: ['fas', 'sailboat'] },
  { column: 'waterfront', icon: ['fas', 'water'] },
  { column: 'accessible_property', icon: ['fas', 'wheelchair'] },
  { column: 'villa_product', icon: ['fas', 'house-tree'] },
  { column: 'all_suite', icon: ['fas', 'bed'] },
  { column: 'family_friendly', icon: ['fas', 'family'] },
  { column: 'non_smoking_resort', icon: ['fas', 'ban-smoking'] },
  { column: 'downtown', icon: ['fas', 'city'] },
  { column: 'on_strip', icon: ['fas', 'road'] },
  { column: 'four_star', icon: ['fas', 'star'] },
  { column: 'five_star', icon: ['fas', 'star'] },
  { column: 'four_star_diamond', icon: ['fas', 'gem'] },
  { column: 'five_star_diamond', icon: ['fas', 'gem'] },
];

// Columns are flags; anything other than empty or 0 marks the amenity as present.
export function getListingAmenities(listing: FreeHotelItem): Amenity[] {
  return AMENITIES.flatMap(({ column, icon }) => {
    const value = listing[column];
    const text = isNil(value) ? '' : value.toString().trim();
    if (text === '' || Number(text) === 0) return [];
    return [{ column, icon, label: i18n.translate(`directoryView.amenities.${column}`) as string }];
  });
}
